import { EmployeeListItem } from "@/components/employee/EmployeeListItem.tsx";
import { EventEditModal } from "@/components/event/EventEditModal.tsx";
import { Search } from "@/components/search/Search.tsx";
import useEmployeeSearchInline, { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { ApiDepartment } from "@/types/department.types.ts";
import { ApiEmployeeFilter } from "@/types/employees/employee.types.ts";
import { getMockEventEdit } from "@/types/event.types.ts";
import { RuleApplySignType, RuleConditionType } from "@/types/rule.types.ts";
import {
  Box,
  BoxWithScroll,
  Button,
  ButtonIcon,
  DropdownMenu,
  DropdownMenuItem,
  EmptyStateBase,
  FontAwesomeIcon,
  PaginationBlock,
  Skeleton,
  Stack,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "react-use";

interface DepartmentViewRightPaneProps {
  department: ApiDepartment;
  employeeFilter: ApiEmployeeFilter;
  onClose: () => void;
}

export function DepartmentViewRightPane({
  department,
  employeeFilter,
  onClose,
}: DepartmentViewRightPaneProps) {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { data: me } = useEmployeeMe();
  const [searchTerm, setSearchTerm] = useState("");

  const handleCreateMeeting = () => {
    if (!me) return;

    const eventToEdit = getMockEventEdit(me);
    eventToEdit.attendeeGroups = [
      {
        rules: [
          {
            condition_type: RuleConditionType.department,
            sign: RuleApplySignType.include,
            condition_ids: [department.id],
            condition_entities: [{ id: department.id, title: department.title }],
            value_entities: [],
          },
        ],
      },
    ];

    openModal(EventEditModal, { event: eventToEdit });
  };

  const { data, setFilter, filter, isLoading } = useEmployeeSearchInline(true, {
    ...employeeFilter,
    page_number: 1,
    text: "",
    department_ids: [department.id],
    with_child_departments: true,
  });

  useDebounce(
    () => {
      setFilter({
        ...filter,
        text: searchTerm,
      });
    },
    300,
    [searchTerm]
  );

  return (
    <Stack className={"h-full"}>
      <Stack
        direction={"horizontal"}
        justify={"between"}
        items={"center"}
        className={"px-6 pt-6 pb-4"}
      >
        <Stack direction={"horizontal"} items={"center"} gap={"sm"}>
          <Title header={department.title} size={"md"} />
          <Title
            className={"text-secondary"}
            size={"md"}
            header={`(${department.employees_count})`}
          />
        </Stack>
        <Stack direction={"horizontal"} items={"center"} gap={"sm"}>
          <DropdownMenu
            placement={"bottom-end"}
            trigger={
              <Button variant={"secondary"}>
                {t("calendar:actions")}
                <FontAwesomeIcon icon={"fa-light fa-chevron-down"} />
              </Button>
            }
          >
            <DropdownMenuItem
              title={t("calendar:create_meeting")}
              description={t("calendar:create_meeting_for_department", {
                name: department.title,
              })}
              onClick={handleCreateMeeting}
            />
          </DropdownMenu>
          <ButtonIcon
            variant={"plain"}
            icon={<FontAwesomeIcon icon={"fa-light fa-xmark"} />}
            onClick={onClose}
          />
        </Stack>
      </Stack>

      <BoxWithScroll className={"grow"}>
        <Box className={"px-6 pb-6"}>
          {department.manager && (
            <>
              <EmployeeListItem
                to={`/employees/${department.manager.id}`}
                blank={true}
                employee={department.manager}
                captionSlot={<Text className={"text-new font-semibold"}>{t("core:manager")}</Text>}
              />
              <hr />
            </>
          )}

          <Search
            placeholder={t("core:name")}
            value={searchTerm}
            onInput={(e) => {
              setSearchTerm(e.currentTarget.value);
            }}
            type={"text"}
          />

          {isLoading && <Skeleton className={"h-8 w-full"} />}
          {data?.items.map(
            (employee) =>
              (employee.id != department.manager?.id || searchTerm) && (
                <EmployeeListItem
                  key={`department_employee_${employee.id}`}
                  to={`/employees/${employee.id}`}
                  blank={true}
                  employee={employee}
                />
              )
          )}

          {!isLoading && !data?.metadata.total_items && (
            <EmptyStateBase emoji={":sloth:"} title={t("common:nothing_found")} />
          )}

          {data && data.metadata.total_pages > 1 && (
            <PaginationBlock
              metadata={data?.metadata}
              onPageChange={(page) => {
                setFilter({ ...filter, page_number: page });
              }}
            />
          )}
        </Box>
      </BoxWithScroll>
    </Stack>
  );
}
